import moment from 'moment';
import { fetchAvailabilityByDateAndDuration } from './availabilityActions';
import { removeTimeFromMoments } from '../utils/timeUtils';

export const SELECT_DAY = 'SELECT_DAY';
export const SELECT_DAY_AND_SERVICE = 'SELECT_DAY_AND_SERVICE';
export const SELECT_SERVICE = 'SELECT_SERVICE';
export const SET_TIME_RANGE = 'SET_TIME_RANGE';
export const SET_DURATION = 'SET_DURATION';

// Helpers

// Round to next nearest 30 min interval
function roundUpToNextHalfHour(datetime) {
  const remainder = 30 - (datetime.minute() % 30);
  return moment(datetime).add(remainder, 'minutes');
}

// If the start of day is < 18 hours in the future, we adjust to ask for
// availability 18 hours in the future since that is the minimum buffer time
function adjustedStartTime() {
  const adjusted = moment()
    .add(18, 'hours')
    .startOf('minute');

  // Blocks of time must fall on 30 min boundary
  return roundUpToNextHalfHour(adjusted);
}

function getStartOfDay(day) {
  const startOfDay = day.clone().startOf('day');
  if (startOfDay.diff(moment(), 'hours') < 18) {
    const startDateTime = adjustedStartTime();

    // If we are adjusting, we want the start of the next day to be the end point
    const endDateTime = startDateTime
      .clone()
      .add(1, 'days')
      .startOf('day');
    return [startDateTime, endDateTime];
  }

  // If we are not adjusting, just add 24 hours
  const endDateTime = startOfDay.clone().add(1, 'days');
  return [startOfDay, endDateTime];
}

export function selectDayFromUserObject(user) {
  return (dispatch, getState) => {
    const freeDate = user && user.freeDate ? moment(user.freeDate) : null;
    const today = removeTimeFromMoments(moment());
    const day = !freeDate || freeDate.isBefore(today) ? moment().add(1, 'day') : freeDate;

    dispatch({
      type: SELECT_DAY,
      payload: day
    });

    // get the state, selectedServiceId and durationInMinutes filters
    const state = getState();
    const { selectedService, durationInMinutes } = state.filters;

    // if all required data is present, call fetchServiceAvailability
    // this prevents hitting the endpoint multiple times during
    // state initialization
    if (selectedService.id !== null) {
      const [start, end] = getStartOfDay(day);

      // dispatch a call to fetchAvailabilityByDateAndDuration
      // with updated data
      dispatch(
        fetchAvailabilityByDateAndDuration(selectedService.id, start, end, durationInMinutes)
      );
    }
  };
}

// day is expected to be a moment object
export function selectDay(day) {
  return (dispatch, getState) => {
    // immediately dispatch SELECT_DAY
    dispatch({
      type: SELECT_DAY,
      payload: day
    });

    // get the state, selectedServiceId and durationInMinutes filters
    const state = getState();
    const { selectedService, durationInMinutes } = state.filters;

    // if all required data is present, call fetchServiceAvailability
    // this prevents hitting the endpoint multiple times during
    // state initialization
    if (selectedService.id !== null) {
      const [start, end] = getStartOfDay(day);

      // dispatch a call to fetchAvailabilityByDateAndDuration
      // with updated data
      dispatch(
        fetchAvailabilityByDateAndDuration(selectedService.id, start, end, durationInMinutes)
      );
    }
  };
}

// expect selectedServiceId to be an Integer value
export function selectService(service) {
  return (dispatch, getState) => {
    dispatch({
      type: SELECT_SERVICE,
      payload: service
    });

    // get the state, selectedDay and durationInMinutes filters
    const state = getState();
    const { selectedDay, durationInMinutes } = state.filters;

    // if all required data is present, call fetchServiceAvailability
    // this prevents hitting the endpoint multiple times during
    // state initialization
    if (selectedDay !== null && durationInMinutes !== null) {
      const [start, end] = getStartOfDay(selectedDay);

      // dispatch a call to fetchAvailabilityByDateAndDuration
      // with updated data
      dispatch(fetchAvailabilityByDateAndDuration(service.id, start, end, durationInMinutes));
    }
  };
}

// expect selectedServiceId to be an Integer value
export function selectDayAndService(day, service) {
  return (dispatch, getState) => {
    dispatch({
      type: SELECT_DAY_AND_SERVICE,
      payload: {
        day,
        service
      }
    });

    // get the state, selectedDay and durationInMinutes filters
    const state = getState();
    const { durationInMinutes } = state.filters;

    // if all required data is present, call fetchServiceAvailability
    // this prevents hitting the endpoint multiple times during
    // state initialization
    if (durationInMinutes !== null) {
      const [start, end] = getStartOfDay(day);

      // dispatch a call to fetchAvailabilityByDateAndDuration
      // with updated data
      dispatch(fetchAvailabilityByDateAndDuration(service.id, start, end, durationInMinutes));
    }
  };
}

// expect durationInMinutes to be an Integer value
export function setDuration(durationInMinutes) {
  return (dispatch, getState) => {
    dispatch({
      type: SET_DURATION,
      payload: durationInMinutes
    });

    // get the state, selectedDay and selectedServiceId filters
    const state = getState();
    const { selectedDay, selectedService } = state.filters;

    // if all required data is present, call fetchServiceAvailability
    // this prevents hitting the endpoint multiple times during
    // state initialization
    if (selectedDay !== null && selectedService.id !== null) {
      const [start, end] = getStartOfDay(selectedDay);

      // dispatch a call to fetchAvailabilityByDateAndDuration
      // with updated data
      dispatch(
        fetchAvailabilityByDateAndDuration(selectedService.id, start, end, durationInMinutes)
      );
    }
  };
}

// expect timeRange to be an object
// where the key is time of day in minutes:
// {
//   timeRangeStart: { hours:  0, minutes: 45, sliderValue: 45 },
//   timeRangeEnd:   { hours: 24, minutes: 0,  sliderValue: 1440 }
// }
/*
export function setTimeRange(timeRange) {
  return {
    type: SET_TIME_RANGE,
    payload: timeRange
  };
}
*/
// TODO: We may want to eventually do this filtering locally to make it faster
// by dispatching the above and iterating through the current teacher list
// but for now it's more code to maintain and better to make this optimization
// post onsched migration
export function setTimeRange(timeRange) {
  return (dispatch, getState) => {
    dispatch({
      type: SET_TIME_RANGE,
      payload: timeRange
    });

    // get the state, selectedDay and selectedServiceId filters
    const state = getState();
    const { selectedDay, selectedService, durationInMinutes } = state.filters;

    // If all required data is present, call fetchServiceAvailability
    // this prevents hitting the endpoint multiple times during
    // state initialization
    if (selectedDay !== null && selectedService.id !== null) {
      const startOfDay = selectedDay.clone().startOf('day');

      let startTime = startOfDay
        .clone()
        .add(timeRange.timeRangeStart.hours, 'hours')
        .add(timeRange.timeRangeStart.minutes, 'minutes');

      if (startTime.diff(moment(), 'hours') < 18) startTime = adjustedStartTime();

      const endTime = startOfDay
        .clone()
        .add(timeRange.timeRangeEnd.hours, 'hours')
        .add(timeRange.timeRangeEnd.minutes, 'minutes');

      // dispatch a call to fetchAvailabilityByDateAndDuration
      // with updated data
      dispatch(
        fetchAvailabilityByDateAndDuration(
          selectedService.id,
          startTime,
          endTime,
          durationInMinutes
        )
      );
    }
  };
}



// WEBPACK FOOTER //
// ./src/actions/filterActions.js